import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { canonicalFeedbackPreviewEnabled } from "@/features/feedback-integration/feedbackPreview";
import { isArchivedTeam } from "@/features/navigation/seasonGroups";
import { useScopeStore } from "@/stores/scope";

/**
 * Coach workspace sub-tab strip — the shared nav across the
 * `/coach` list pages and `/coach/review`.
 *
 * Ports the legacy `setCoachTab` tab strip (`js/coaching.js`) and
 * layers the player-first Board in front of it. The current product
 * collapses object-type library tabs into one Feedback Library tab,
 * and folds follow-up work into Board instead of exposing a second
 * attention surface. Goals is deliberately NOT a tab; goal cards
 * live inside Players.
 *
 * Routing shape (M6a, non-disruptive to M4/M5):
 *   - `/coach` — Phase 1 Team Feedback Board landing.
 *   - `/coach?tab=roster|library|matches|settings`
 *     — the tabbed shell renders the matching list page. `roster`
 *     remains an explicit deep link during the transition.
 *   - `/coach/review` stays its OWN path (the M4/M5 surface, with
 *     its `match_id`/`note_id`/`clip_id` schema untouched). The
 *     "Review" tab is a link to that path, not a `?tab=review`. In
 *     canonical builds it opens the Resource editor's kind chooser
 *     instead (resource-editor-redesign PR 3).
 *
 * Both surfaces render this strip so the workspace feels unified.
 * The active tab is computed from the current location, passed in
 * by each host (the shell knows its `?tab=`; Coach Review passes
 * `"review"`).
 */

export type CoachTab =
  | "board"
  | "roster"
  | "matches"
  | "library"
  | "settings"
  | "review";

/** Tab order + labels. Board leads the strip because bare `/coach`
 * now lands on the Team Feedback Board. The Players tab keeps the
 * legacy `tab=roster` route value so old deep links keep working. */
const TABS: ReadonlyArray<{ id: CoachTab; label: string }> = [
  { id: "board", label: "Board" },
  { id: "roster", label: "Players" },
  { id: "review", label: "Review" },
  { id: "matches", label: "Matches" },
  { id: "library", label: "Feedback Library" },
  { id: "settings", label: "Settings" },
];

export const OPERATIONAL_COACH_TABS = [
  "roster",
  "matches",
  "settings",
] as const satisfies ReadonlyArray<CoachTab>;

export const ALL_COACH_TABS = TABS.map((tab) => tab.id);

export type CoachTabStripProps = {
  /** The currently-active tab — the host computes it (the shell
   * from its `?tab=`, Coach Review hardcodes `"review"`). */
  active: CoachTab;
  visibleTabs?: ReadonlyArray<CoachTab>;
};

export function CoachTabStrip({ active, visibleTabs }: CoachTabStripProps) {
  const strip = useRef<HTMLElement>(null);
  const revealTab = (element: HTMLElement) => {
    const container = strip.current;
    if (!container) return;
    const left = element.getBoundingClientRect().left - container.getBoundingClientRect().left + container.scrollLeft;
    if (left < container.scrollLeft) container.scrollLeft = left;
    else if (left + element.offsetWidth > container.scrollLeft + container.clientWidth)
      container.scrollLeft = left + element.offsetWidth - container.clientWidth;
  };
  useEffect(() => {
    const container = strip.current;
    if (!container) return;
    const revealSelected = () => {
      const selected = container.querySelector<HTMLElement>('[aria-selected="true"]');
      if (selected) revealTab(selected);
    };
    revealSelected();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(revealSelected);
    observer.observe(container);
    return () => observer.disconnect();
  }, [active, visibleTabs]);
  const visible = visibleTabs ? new Set(visibleTabs) : null;
  // Canonical builds: the Review tab opens the Resource kind chooser, so it is
  // named for that and, like Library New resource, absent on an archived team.
  const canonical = canonicalFeedbackPreviewEnabled();
  const archivedTeam = useScopeStore((state) => isArchivedTeam(state.active?.team));
  return (
    <nav
      ref={strip}
      className="coach-workspace-tabs"
      onKeyDown={(event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        const tabs = Array.from(strip.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []);
        const index = tabs.indexOf(document.activeElement as HTMLElement);
        if (index < 0 || !tabs.length) return;
        event.preventDefault();
        const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1
          : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].focus({ preventScroll: true });
        revealTab(tabs[next]);
      }}
      role="tablist"
      aria-label="Coach workspace"
      data-testid="coach-tab-strip"
    >
      {TABS.filter((tab) => (!visible || visible.has(tab.id)) && !(canonical && archivedTeam && tab.id === "review")).map((tab) => {
        const isActive = tab.id === active;
        // Review is its own path; the list pages are `/coach?tab=`.
        const linkProps =
          tab.id === "review"
            ? canonicalFeedbackPreviewEnabled()
              ? ({ to: "/coach/resources/$resourceId", params: { resourceId: "new" } } as const)
              : ({ to: "/coach/review" } as const)
            : tab.id === "board"
              ? ({ to: "/coach", search: {} } as const)
            : tab.id === "roster"
              ? ({ to: "/coach", search: { tab: "roster" } } as const)
              : ({ to: "/coach", search: { tab: tab.id } } as const);
        return (
          <Link
            key={tab.id}
            {...linkProps}
            role="tab"
            aria-selected={isActive}
            data-coach-tab={tab.id}
            data-active={isActive}
            onFocus={(event) => revealTab(event.currentTarget)}
            className="coach-workspace-tab"
          >
            {canonical && tab.id === "review" ? "New resource" : tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
